/** Demo Mode only - reuses the standard dashboard metric tile anatomy (see
 * .github/instructions/frontend.instructions.md "Dashboard Metric Tiles") for every
 * headline number shown across the Finance area, so tiles look identical to the rest
 * of the app rather than introducing a bespoke finance tile style. */

import { Box, SvgIcon, useTheme } from '@mui/material';
import { Section } from '../../../components/ui/Section';

export interface MetricTileProps {
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

/** Formats a variance so positive/negative is legible without relying on colour alone -
 * explicit +/- sign, and brackets for negative values (finance convention). */
export const formatSignedVariance = (value: number, formatted: string): string => (value < 0 ? `(${formatted.replace('-', '')})` : `+${formatted}`);

export const formatSignedPoints = (points: number): string => `${points >= 0 ? '+' : '-'}${Math.abs(points).toFixed(1)}pts`;

const MetricTile = ({ label, value, subtitle, icon, color }: MetricTileProps) => {
  const theme = useTheme();
  return (
    <Section style={{ p: 4, mb: 0 }}>
      <Box display="flex"
        alignItems="center"
        justifyContent="space-between">
        <Box flex={1}>
          <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
            {label}
          </Box>
          <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
            {value}
          </Box>
          {subtitle && (
            <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
              {subtitle}
            </Box>
          )}
        </Box>
        <Box sx={{ backgroundColor: `${color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
          <SvgIcon fontSize="medium">{icon}</SvgIcon>
        </Box>
      </Box>
    </Section>
  );
};

export default MetricTile;
